"use client";

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { getMyModuleChapter } from "@/lib/module-chapters.functions";
import { getMyProductCards } from "@/lib/product-cards.functions";
import { collectProductKeys } from "@/lib/module-chapters/parseChapter";
import { ChapterBody } from "@/components/cockpit/ChapterBody";
import { ChapterToc } from "@/components/cockpit/ChapterToc";
import { PHASE_TITLES } from "@/lib/hundert-tage";
import { CockpitPageHeader } from "@/components/cockpit/CockpitPageHeader";

export const Route = createFileRoute("/_cockpit/cockpit/hundert-tage/kapitel/$slug")({
  component: ChapterView,
});

/**
 * Ticket Z7a: Kapitelseite.
 *
 * Ergänzung 2 zum Ticket: Ein deaktiviertes oder unbekanntes Kapitel ist nicht
 * erreichbar – der Aufruf leitet auf die Kapitelübersicht um.
 */
function ChapterView() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const fetchChapter = useServerFn(getMyModuleChapter);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["cockpit", "hundert-tage", "chapter", slug],
    queryFn: () => fetchChapter({ data: { slug } }),
  });

  // Ticket Z12: Produktkarten werden getrennt aufgelöst; nicht auflösbare,
  // deaktivierte oder bereits gebuchte Leistungen fehlen im Ergebnis.
  const fetchCards = useServerFn(getMyProductCards);
  const productKeys = data?.body ? collectProductKeys(data.body) : [];
  const { data: cards } = useQuery({
    queryKey: ["cockpit", "hundert-tage", "produktkarten", slug, productKeys.join(",")],
    queryFn: () => fetchCards({ data: { keys: productKeys } }),
    enabled: productKeys.length > 0,
  });

  const missing = !isLoading && !isError && !data;

  useEffect(() => {
    if (missing) {
      void navigate({ to: "/cockpit/hundert-tage/kapitel", replace: true });
    }
  }, [missing, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Kapitel wird geladen …
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-muted-foreground">
        Das Kapitel konnte nicht geladen werden. Bitte versuchen Sie es später erneut.
      </p>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4">
      <Link
        to="/cockpit/hundert-tage"
        className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Zurück zur Übersicht
      </Link>

      {/* Einheitlicher Seitenkopf: der Kapiteltitel ist die größte Überschrift
          der Seite, die Zwischenüberschriften im Text liegen darunter. */}
      <div>
        <p className="text-xs text-muted-foreground">Kapitel {data.chapterNumber}</p>
        <CockpitPageHeader title={data.title} />
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_16rem]">
        <article className="order-2 lg:order-1">
          <ChapterBody body={data.body} productCards={cards ?? []} />

          {data.phaseKey && (
            <Link
              to="/cockpit/hundert-tage"
              search={{ phase: data.phaseKey }}
              hash="hundert-tage-aufgaben"
              className="mt-10 inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-brand-button-foreground"
            >
              Zu den Aufgaben: {PHASE_TITLES[data.phaseKey]}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          )}
        </article>

        <div className="order-1 lg:order-2">
          <ChapterToc body={data.body} />
        </div>
      </div>
    </div>
  );
}
